// ---------------------------------------------------------------------------
// HybridRepository — CogoKnowledgeRepository that routes per-method between
// LocalWasmRepository (fast path) and injected server fallbacks.
//
// Phase 1b Step 1b.5 per .planning/kg-repository-interface.md v1.0 §12.5.
//
// Routing summary:
//
//   | Method               | Primary | Fallback condition                  |
//   | -------------------- | ------- | ----------------------------------- |
//   | findExamples         | Local   | none (no server endpoint today)     |
//   | getDesignSystem      | Local   | data == null && dsFallback present  |
//   | listDesignSystems    | Local   | data.length === 0 && dsFallback     |
//   | getSemanticTypes     | Local   | none                                |
//   | getActions           | Local   | none                                |
//   | getEvents            | Local   | none                                |
//   | validate             | Local   | none                                |
//   | recordObservation    | server  | throws when no fallback available   |
//   | syncObservations     | Local   | Task 11 MVP local-only              |
//   | saveObservations     | Local   | Task 11 MVP local-only              |
//   | findObservations     | Local   | Task 11 MVP local-only              |
//   | registerExample      | server  | throws when no fallback available   |
//   | upsertDesignSystem   | server  | throws when no fallback available   |
//
// BroadcastChannel('cogo-kg-sync') cache invalidation is explicitly
// deferred to Phase 2 Step 2.4 per §12.5 — this class focuses on
// routing only.
//
// Full-Repository-shaped server endpoints (e.g. cogo.examples.* /
// cogo.actions.* / cogo.events.* / cogo.validate.*) do NOT exist on
// cogo-mcp-gateway today — Phase 2b scope. Until then, methods without
// a server fallback stay local-only.
// ---------------------------------------------------------------------------

import type {
  Action,
  DesignSystem,
  Event,
  Example,
  FindObservationsQuery,
  I18n,
  Narrative,
  Observation,
  ObservationRecord,
  Pattern,
  Result,
  SaveResult,
  SemanticType,
  SyncResult,
  ThemeV2Record,
  Tokens,
  ValidationPhase,
  ValidationReport,
} from "./types"

import type {
  CogoKnowledgeRepository,
  FindExamplesQuery,
  GetActionsFilter,
  GetEventsFilter,
  StoreScopedValidation,
} from "./repository"

import type { LocalWasmRepository } from "./local-wasm-repository"
import type {
  ExpressionAccessorItem,
  ExpressionPrefixItem,
  UiActionVocabularyItem,
  UiEventVocabularyItem,
} from "./vocabulary"
import type {
  KgDesignSystemFallback,
  KgServerFallback,
} from "./server-http/types"

export interface HybridRepositoryOpts {
  /**
   * Server fallback for the Phase 1a 3-method shape (search_components /
   * get_component / get_stats). CURRENTLY NOT USED by the Repository
   * surface — Repository has no searchComponents/getComponent/getStats
   * methods. Kept here so callers can thread the single fallback through
   * for a future legacy-client helper and so HybridRepository stays a
   * single-constructor API across PR C + PR D + PR E.
   */
  componentFallback?: KgServerFallback
  /**
   * Server fallback for DS list/get. When injected, HybridRepository
   * routes `getDesignSystem(id)` / `listDesignSystems()` to this
   * fallback when local returns empty.
   */
  dsFallback?: KgDesignSystemFallback
}

export class HybridRepository
  implements CogoKnowledgeRepository, StoreScopedValidation
{
  constructor(
    public readonly local: LocalWasmRepository,
    private readonly opts: HybridRepositoryOpts = {},
  ) {}

  // ——— Lifecycle (pass-through) ———

  async ready(): Promise<void> {
    await this.local.ready()
  }

  versionInfo(): {
    coreVersion: string
    oxigraphVersion: string
    shapeSha: string
  } {
    return this.local.versionInfo()
  }

  // ——— Read methods ———

  findExamples(q: FindExamplesQuery): Promise<Result<Example[]>> {
    return this.local.findExamples(q)
  }

  async getDesignSystem(
    id: string,
  ): Promise<Result<DesignSystem | null>> {
    const local = await this.local.getDesignSystem(id)
    if (local.data !== null) return local
    const fallback = this.opts.dsFallback
    if (!fallback) return local
    const t0 = performance.now()
    const data = await fallback.getDesignSystem(id)
    return {
      data,
      meta: {
        durationMs: Math.round((performance.now() - t0) * 100) / 100,
        source: "server",
      },
    }
  }

  async listDesignSystems(): Promise<Result<DesignSystem[]>> {
    const local = await this.local.listDesignSystems()
    if (local.data.length > 0) return local
    const fallback = this.opts.dsFallback
    if (!fallback) return local
    const t0 = performance.now()
    const data = await fallback.listDesignSystems()
    return {
      data,
      meta: {
        durationMs: Math.round((performance.now() - t0) * 100) / 100,
        source: "server",
      },
    }
  }

  getSemanticTypes(
    ctx: { category?: string } = {},
  ): Promise<Result<SemanticType[]>> {
    return this.local.getSemanticTypes(ctx)
  }

  // ——— Vocabulary read surface (local-only; no server endpoint exists) ———

  getExpressionPrefixes(): Promise<Result<ExpressionPrefixItem[]>> {
    return this.local.getExpressionPrefixes()
  }

  getExpressionAccessors(): Promise<Result<ExpressionAccessorItem[]>> {
    return this.local.getExpressionAccessors()
  }

  getUiEventVocabulary(): Promise<Result<UiEventVocabularyItem[]>> {
    return this.local.getUiEventVocabulary()
  }

  getUiActionVocabulary(): Promise<Result<UiActionVocabularyItem[]>> {
    return this.local.getUiActionVocabulary()
  }

  getNarrative(
    ctx: { dsId: string },
  ): Promise<Result<Narrative | null>> {
    // Local-only in v1. Narratives are additive context — missing narrative
    // does not block generation. Server fallback can be added in v2 if a
    // server-side Narrative authoring surface ships.
    return this.local.getNarrative(ctx)
  }

  getI18n(
    ctx: { locale: string },
  ): Promise<Result<I18n | null>> {
    // Local-only in v1. Same rationale as getNarrative: i18n shapes are
    // additive rendering hints — missing locale degrades to default-locale
    // fallback rather than blocking generation. Server fallback can ship in
    // v2 alongside an authoring surface for per-tenant locale overrides.
    return this.local.getI18n(ctx)
  }

  getTokens(
    ctx: { id: string },
  ): Promise<Result<Tokens | null>> {
    // Local-only in v1. Token axes (spacing/elevation/motion/breakpoints)
    // are additive rendering hints — missing tokens degrade to
    // @cogo/design-presets defaults rather than blocking generation.
    // Server fallback can ship in v2 alongside per-tenant token authoring.
    return this.local.getTokens(ctx)
  }

  getTheme(ctx: { themeId: string }): Promise<{ theme: ThemeV2Record }> {
    // Local-only in v1 — same rationale as getNarrative/getTokens. Theme is
    // a composite over shards that all live locally; server fallback
    // deferred to v2 alongside per-tenant Theme authoring.
    return this.local.getTheme(ctx)
  }

  getPatterns(
    ctx: { domain?: string } = {},
  ): Promise<Result<Pattern[]>> {
    // Local-only in v1. Patterns are additive composition hints — a block
    // that doesn't match any Pattern still renders; Pattern info just
    // augments the LLM's intent context and the Studio inspector. Server
    // fallback can ship in v2 alongside a Pattern-authoring surface.
    return this.local.getPatterns(ctx)
  }

  getActions(filter: GetActionsFilter = {}): Promise<Result<Action[]>> {
    return this.local.getActions(filter)
  }

  getEvents(filter: GetEventsFilter = {}): Promise<Result<Event[]>> {
    return this.local.getEvents(filter)
  }

  // ——— Validation ———

  validate(phase: ValidationPhase): Promise<Result<ValidationReport>> {
    return this.local.validate(phase)
  }

  // ——— StoreScopedValidation (stubs — §12.5 Step 1b.5 note) ———

  loadJsonLd(jsonLd: unknown, graphIri?: string): Promise<string> {
    return this.local.loadJsonLd(jsonLd, graphIri)
  }

  dropGraph(graphIri: string): Promise<void> {
    return this.local.dropGraph(graphIri)
  }

  // ——— Write methods ———
  //
  // Write-side server endpoints exist on cogo-mcp-gateway (e.g. upsert_*
  // tools) but are NOT exposed through the 3-method `KgServerFallback`
  // contract — Phase 2c Data Symbol will add Repository-shaped server
  // write endpoints. For now, all writes delegate to local:
  //  - `recordObservation` / `syncObservations` / `saveObservations` /
  //    `findObservations` — implemented (MVP in-memory + cogo-kg-worker
  //    durable copy)
  //  - `registerExample` / `upsertDesignSystem` — local throws (server-only
  //    by design); HybridRepository would route through a future server
  //    fallback, currently rethrows the local error.

  recordObservation(
    obs: Observation,
  ): Promise<Result<{ id: string }>> {
    return this.local.recordObservation(obs)
  }

  syncObservations(): Promise<Result<SyncResult>> {
    return this.local.syncObservations()
  }

  saveObservations(): Promise<Result<SaveResult>> {
    return this.local.saveObservations()
  }

  findObservations(
    query?: FindObservationsQuery,
  ): Promise<Result<ObservationRecord[]>> {
    return this.local.findObservations(query)
  }

  registerExample(args: {
    template: unknown
    bindingSchema: object
    semanticType: string
    dsId?: string
  }): Promise<Result<{ exampleId: string }>> {
    return this.local.registerExample(args)
  }

  upsertDesignSystem(
    ds: DesignSystem,
  ): Promise<Result<{ dsId: string }>> {
    return this.local.upsertDesignSystem(ds)
  }

  // ——— Diagnostics ———

  /** True if a component-level server fallback is wired. */
  hasComponentFallback(): boolean {
    return !!this.opts.componentFallback
  }

  /** True if a DS server fallback is wired. */
  hasDsFallback(): boolean {
    return !!this.opts.dsFallback
  }

  /** Expose the configured component fallback for legacy KgUnifiedClient wiring (Step 1b.6). */
  getComponentFallback(): KgServerFallback | undefined {
    return this.opts.componentFallback
  }
}
